import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

type PageModule = { default: ComponentType };
// Lazy page that can be preloaded before hydration, so the first client render
// matches the prerendered HTML (no Suspense fallback -> no hydration mismatch).
const page = (loader: () => Promise<PageModule>) => {
  let loaded: ComponentType | null = null;
  const load = () => loader().then((m) => { loaded = m.default; return m; });
  const Lazy = lazy(load);
  const Page = () => { const C = loaded ?? Lazy; return <C />; };
  Page.preload = load;
  return Page;
};

const About = page(() => import("./pages/About"));
const Services = page(() => import("./pages/Services"));
const Contact = page(() => import("./pages/Contact"));
const FAQ = page(() => import("./pages/FAQ"));
const Gallery = page(() => import("./pages/Gallery"));
const Studio = page(() => import("./pages/Studio"));
const NotFound = page(() => import("./pages/NotFound"));

export const preloadRoute = (pathname: string) => {
  const p = pathname.replace(/\/+$/, "") || "/";
  const map: Record<string, { preload: () => Promise<unknown> }> = {
    "/about": About, "/services": Services, "/contact": Contact, "/faq": FAQ, "/gallery": Gallery,
  };
  if (p === "/") return Promise.resolve();
  if (p.startsWith("/admin")) return Promise.resolve();
  return (map[p] ?? NotFound).preload();
};

const createQueryClient = () => new QueryClient();

export const AppProviders = ({
  children,
  helmetContext,
}: {
  children: ReactNode;
  helmetContext?: object;
}) => {
  const queryClient = createQueryClient();

  return (
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
          {children}
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export const AppRoutes = () => (
  <Suspense fallback={null}>
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/about" element={<About />} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/admin/*" element={<Studio />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <AppProviders>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </AppProviders>
);

export default App;
